/* Pack-aware cart linking.
 *
 * Items added to the cart from /pages/crea-tu-pedido (the create-pack
 * page) carry these line-item properties:
 *   _ve_pack_id   — unique id shared by every item of the same pack
 *   _ve_pack_role — "box" for the pack-container product, "bottle" for
 *                   each bottle inside it
 *
 * The cart drawer + /cart template stamp `data-ve-pack-id` and the
 * class `ve-pack-item` onto each row carrying that property. CSS hides
 * the +/- quantity buttons on those rows so the pack stays coherent
 * (1 box + 3 bottles, no individual edits).
 *
 * What this file does, in order of importance:
 *
 *   1. Intercept clicks on remove/+/- controls of pack items
 *      *anywhere* in the site (cart drawer, cart page, home best-
 *      sellers card stepper). Instead of letting the mutation run,
 *      pop up a confirmation modal ("Eliminate the whole pack?"). If
 *      the user confirms, do ONE /cart/update.js call that zeros out
 *      every line item of the pack and renders the cart sections in
 *      the same response. If they cancel, nothing happens. This is
 *      the design that kills race conditions: the mutation never
 *      starts until we have user intent.
 *
 *   2. Re-stamp data-ve-pack-* attributes onto cart rows whenever
 *      Dawn re-renders the cart sections (via PUB_SUB_EVENTS.cartUpdate
 *      subscription, NOT a window.fetch monkey-patch). Keeps the row
 *      classification in sync after Shopify swaps DOM.
 *
 *   3. One-time orphan cleanup at page load. If for some reason the
 *      cart contains a pack with a missing box, missing bottles, or
 *      bottle-count != 3 (DOM tampering, broken session, etc.), zero
 *      out the leftovers so the cart is never inconsistent.
 *
 * Loaded globally from layout/theme.liquid (after assets/global.js
 * which defines subscribe/publish/PUB_SUB_EVENTS).
 */
(() => {
  if (window.__vePackCartLinkBound) return;
  window.__vePackCartLinkBound = true;

  const PROP_PACK_ID = '_ve_pack_id';
  const PROP_PACK_ROLE = '_ve_pack_role';
  /* Every pack created from /pages/crea-tu-pedido is "1 box + exactly
     3 bottles". Encode that here so the orphan cleanup can detect a
     pack that has been tampered with externally. If the business ever
     ships a different pack size, the cleanest fix is to add a
     `_ve_pack_size` property on the box and read that instead. */
  const EXPECTED_BOTTLES_PER_PACK = 3;

  let isCascading = false;

  /* ─── Cart API helpers ──────────────────────────────── */

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not fetch cart');
    return res.json();
  }

  /* Single update call that also returns the freshly-rendered cart
     sections in the same response — no follow-up reload needed. Same
     pattern Dawn's cart-items.js uses for its own line item mutations.
     We pass `sections_url: window.location.pathname` so Shopify renders
     the sections in the context of the page the user is on (cart page
     vs any other), which matters for the `cart-icon-bubble` section. */
  async function updateCartAndRender(updates) {
    const drawerEl = document.querySelector('cart-drawer');
    const sectionIds = drawerEl && typeof drawerEl.getSectionsToRender === 'function'
      ? drawerEl.getSectionsToRender().map((s) => s.id)
      : ['cart-icon-bubble', 'cart-drawer'];

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('Cart update failed');
    const response = await res.json();

    if (drawerEl && typeof drawerEl.renderContents === 'function') {
      drawerEl.renderContents(response, true);
    }

    /* Notify other listeners (cart-items on /cart, cart-notification, etc.)
       so they re-hydrate from the same response. Tag the source so our
       own subscriber skips its re-decoration pass to avoid loops. */
    try {
      // eslint-disable-next-line no-undef
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 've-pack-cascade',
        cartData: response,
      });
    } catch (_) {
      /* publish/PUB_SUB_EVENTS not loaded — non-fatal */
    }

    return response;
  }

  function groupItemsByPack(cart) {
    const packs = {};
    cart.items.forEach((item) => {
      const props = item.properties || {};
      const packId = props[PROP_PACK_ID];
      if (!packId) return;
      if (!packs[packId]) {
        packs[packId] = { id: packId, box: null, bottles: [] };
      }
      const role = props[PROP_PACK_ROLE];
      if (role === 'box') packs[packId].box = item;
      else if (role === 'bottle') packs[packId].bottles.push(item);
      else packs[packId].bottles.push(item);
    });
    return packs;
  }

  /* ─── Confirmation modal ────────────────────────────── */

  let modalEl = null;

  function ensureModal() {
    if (modalEl) return modalEl;
    modalEl = document.createElement('dialog');
    modalEl.id = 've-pack-confirm-modal';
    modalEl.className = 've-pack-confirm-modal';
    modalEl.innerHTML = `
      <div class="ve-pack-confirm-modal__content">
        <h3 class="ve-pack-confirm-modal__title">¿Eliminar el pack completo?</h3>
        <p class="ve-pack-confirm-modal__body">
          Este producto es parte de un pack armado en <strong>Crea tu pack</strong>.
          Para mantener la coherencia del pack, sacar o modificar este item
          va a eliminar la caja y las 3 botellas juntas del carrito.
        </p>
        <div class="ve-pack-confirm-modal__actions">
          <button type="button" class="ve-pack-confirm-modal__cancel">Cancelar</button>
          <button type="button" class="ve-pack-confirm-modal__confirm">Sí, eliminar pack</button>
        </div>
      </div>
    `;
    document.body.appendChild(modalEl);
    return modalEl;
  }

  function askConfirmation() {
    const dialog = ensureModal();
    return new Promise((resolve) => {
      const confirmBtn = dialog.querySelector('.ve-pack-confirm-modal__confirm');
      const cancelBtn = dialog.querySelector('.ve-pack-confirm-modal__cancel');

      let settled = false;
      const cleanup = (result) => {
        if (settled) return;
        settled = true;
        confirmBtn.removeEventListener('click', onConfirm);
        cancelBtn.removeEventListener('click', onCancel);
        dialog.removeEventListener('close', onClose);
        if (dialog.open) dialog.close();
        resolve(result);
      };
      const onConfirm = () => cleanup(true);
      const onCancel = () => cleanup(false);
      const onClose = () => cleanup(false);

      confirmBtn.addEventListener('click', onConfirm);
      cancelBtn.addEventListener('click', onCancel);
      dialog.addEventListener('close', onClose);
      dialog.showModal();
      confirmBtn.focus();
    });
  }

  /* ─── Cascade remove ────────────────────────────────── */

  async function cascadeRemovePack(packId) {
    if (isCascading) return;
    isCascading = true;
    try {
      const cart = await fetchCart();
      const updates = {};
      cart.items.forEach((item) => {
        if ((item.properties || {})[PROP_PACK_ID] === packId) {
          updates[item.key] = 0;
        }
      });
      if (Object.keys(updates).length === 0) return;
      await updateCartAndRender(updates);
    } catch (err) {
      console.error('[ve-pack] cascade failed', err);
    } finally {
      isCascading = false;
    }
  }

  /* ─── Click interception ────────────────────────────── */

  function findActionButton(target) {
    /* Only intercept controls inside the cart drawer / cart page row.
       Home product card steppers (`.ve-builder-card__control`) are
       handled separately by assets/home-best-sellers.js, which now
       filters out items carrying _ve_pack_id so it cannot touch pack
       lines (see commits 2024-05). That means the home stepper's +/-
       always operates on stand-alone (non-pack) line items, so we
       don't need to intercept it here. Letting it through avoids
       falsely popping the "delete pack" modal when the customer
       legitimately wants to add a separate bottle of the same variant. */
    return target.closest(
      [
        'cart-remove-button a',
        'cart-remove-button button',
        'button[name="remove"]',
        '.cart-remove-button',
        '.cart-item .quantity__button',
        '[data-ve-pack-id] .quantity__button',
      ].join(',')
    );
  }

  function findPackIdForButton(btn) {
    /* Cart drawer / cart page rows carry data-ve-pack-id via our
       decoratePackRows pass. No DOM-less fallback is needed because we
       no longer intercept clicks on home product cards. */
    const cartRow = btn.closest('[data-ve-pack-id]');
    return cartRow ? cartRow.dataset.vePackId : null;
  }

  document.addEventListener(
    'click',
    async (e) => {
      const btn = findActionButton(e.target);
      if (!btn) return;
      if (isCascading) return;

      const packId = findPackIdForButton(btn);
      if (!packId) return; /* Not a pack item — let the default handler run */

      e.preventDefault();
      e.stopPropagation();

      const confirmed = await askConfirmation();
      if (confirmed) await cascadeRemovePack(packId);
    },
    true /* capture phase: intercept BEFORE Shopify's handlers */
  );

  /* ─── Re-decoration ─────────────────────────────────── */

  async function decoratePackRows() {
    try {
      const cart = await fetchCart();
      document.querySelectorAll('.cart-item').forEach((row) => {
        if (!row.id) return;
        const m = row.id.match(/-Item-(\d+)$/);
        if (!m) return;
        const idx = parseInt(m[1], 10) - 1;
        const item = cart.items[idx];
        if (!item) return;
        const packId = (item.properties || {})[PROP_PACK_ID];
        if (!packId) {
          row.classList.remove('ve-pack-item', 've-pack-item--box', 've-pack-item--bottle');
          delete row.dataset.vePackId;
          delete row.dataset.vePackRole;
          delete row.dataset.veLineKey;
          return;
        }
        const role = (item.properties || {})[PROP_PACK_ROLE] || '';
        row.classList.add('ve-pack-item');
        if (role) row.classList.add(`ve-pack-item--${role}`);
        row.dataset.vePackId = packId;
        row.dataset.vePackRole = role;
        row.dataset.veLineKey = item.key;
      });
    } catch (_) {
      /* Non-fatal: without decoration, the click interceptor still
         works via the cart.js fallback inside findPackIdForButton. */
    }
  }

  /* Subscribe to Dawn's cart-update event so we re-decorate after
     any mutation completes. Skip our own cascade events to avoid
     re-running on a refresh we just triggered. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 've-pack-cascade') return;
      window.setTimeout(decoratePackRows, 50);
    });
  }

  /* ─── One-time orphan cleanup at page load ─────────── */

  async function cleanupOrphans() {
    if (isCascading) return false;
    try {
      const cart = await fetchCart();
      const packs = groupItemsByPack(cart);
      const updates = {};

      Object.values(packs).forEach((group) => {
        const hasBox = !!group.box;
        const bottleCount = group.bottles.reduce(
          (sum, b) => sum + (b.quantity || 0),
          0
        );
        if (!hasBox || bottleCount !== EXPECTED_BOTTLES_PER_PACK) {
          if (group.box) updates[group.box.key] = 0;
          group.bottles.forEach((b) => (updates[b.key] = 0));
          return;
        }
        if (group.box.quantity !== 1) updates[group.box.key] = 1;
      });

      if (Object.keys(updates).length === 0) return false;
      isCascading = true;
      await updateCartAndRender(updates);
      return true;
    } catch (err) {
      console.error('[ve-pack] cleanup failed', err);
      return false;
    } finally {
      isCascading = false;
    }
  }

  /* ─── Boot ──────────────────────────────────────────── */

  function boot() {
    decoratePackRows();
    cleanupOrphans();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
